import { useMutation, useQuery } from '@apollo/client/react';
import { Accordion, AbsoluteCenter, Box, BoxProps, Center, ProgressCircle, Heading, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { AppLayoutContextValue, useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton';
import { PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT } from '@src/components/sco/InvestorMatching/InvestorMatchPitchBuilder';
import { USER_SEARCH_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment';
import { requestBuilderSections } from '@src/components/sco/Requests/RequestBuilder/FormSections';
import { REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT } from '@src/components/sco/Requests/RequestBuilder/index';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { gql } from '@src/lib/gql';
import React, { useState } from 'react';
import { LuCheckCheck } from 'react-icons/lu';
import { CheckIcon } from '../../icons';
export const RequestBuilderForms: React.FC<{
  requestsLayout?: AppLayoutContextValue;
  formsContainerProps?: BoxProps;
  hideToolbar?: boolean;
  hidePublish?: boolean;
  groupRequestId: string;
}> = props => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const requestBuilderLayout = useParentAppLayout();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const query = useQuery(gql`
      query ($groupRequestId: String!) {
        groupRequest(groupRequestId: $groupRequestId) {
          ${REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT}
        }
      }
    `, {
    variables: {
      groupRequestId: props.groupRequestId
    }
  });
  const [upsertGroupRequest, upsertGroupRequestMutation] = useMutation(gql`
    mutation ($input: UpsertGroupRequestInput!) {
      upsertGroupRequest(input: $input) {
        groupRequest {
          ${REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT}
        }
      }
    }
  `);
  const [upsertInvestorSearch, upsertInvestorSearchMutation] = useMutation(gql`
    mutation ($input: UpsertInvestorSearchInput!) {
      upsertInvestorSearch(input: $input) {
        investorSearch {
          ${PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT}
        }
      }
    }
  `);
  const [upsertUserSearch, upsertUserSearchMutation] = useMutation(gql`
    mutation ($input: UpsertUserSearchInput!) {
      upsertUserSearch(input: $input) {
        userSearch {
          id
          ${USER_SEARCH_FRAGMENT}
        }
      }
    }
  `);
  const groupRequest = query.data?.groupRequest;
  if (query.loading && !groupRequest) {
    return <Center padding={4} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (query.error) {
    return <Center padding={4} width={'full'}>
        <Text>Error: ${query.error.toString()}</Text>
      </Center>;
  }
  if (!groupRequest) {
    return <Center>Not Loaded</Center>;
  }
  const requestBuilderSectionsToShow = requestBuilderSections.filter(section => {
    if (groupRequest.groupRequestTypeKey && section.groupRequestTypeKeys.includes('all') || section.groupRequestTypeKeys.includes(groupRequest.groupRequestTypeKey)) {
      return true;
    } else {
      return false;
    }
  });
  return <VStack align={'stretch'} colorPalette={'purple'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
      {!props.hideToolbar && <HStack bgColor={'colorPalette.container'} height={'3.5rem'} justify={'space-between'} paddingX={3}>
          <HStack>
            <AppPanelToggleButton appLayout={props.requestsLayout} panelId={'requests-nav'} panelPosition={'left'} />
            <Heading size={'sm'}>Goal Builder</Heading>
          </HStack>
          <HStack color={'colorPalette.border'} gap={2}>
            {upsertGroupRequestMutation.loading ? <>
                <Spinner color={'colorPalette.border'} />
                <Text textStyle={'labelXs'}>Saving</Text>
              </> : <>
                <Icon asChild>
                  <LuCheckCheck />
                </Icon>
                <Text textStyle={'labelXs'}>Saved</Text>
              </>}
            <AppPanelToggleButton appLayout={requestBuilderLayout} panelId={'request-builder-preview'} panelPosition={'right'} />
          </HStack>
        </HStack>}
      <Box flex={1} overflow={'auto'} padding={2} {...props.formsContainerProps}>
        <Accordion.Root bgColor={'transparent'} value={[activeIndex.toString()]} onValueChange={({
        value
      }) => {
        const newVal = value?.[0];
        if (newVal != null) {
          setActiveIndex(parseInt(newVal, 10));
        }
      }}>
          {requestBuilderSectionsToShow.map((section_0, index) => {
          const progress = section_0.getProgress(groupRequest);
          const Component = section_0.FormComponent;
          return <Accordion.Item key={section_0.key} bgColor={activeIndex === index ? 'colorPalette.container' : 'colorPalette.container/50'} border={activeIndex === index ? '1px solid' : 'none'} borderColor={'colorPalette.focusRing'} borderRadius={'md'} marginBottom={2} value={index.toString()}>
                <Accordion.ItemTrigger borderBottom={activeIndex === index ? '1px solid' : 'none'} borderBottomRadius={activeIndex === index ? 'none' : 'lg'} borderColor={'colorPalette.border/50'} padding={4}>
                  <HStack align={'center'} as={'span'} gap={3} width={'full'}>
                    <ProgressCircle.Root colorPalette={'teal'} size={'sm'} strokeLinecap={'round'} value={progress.percentComplete}>
                      <ProgressCircle.Circle css={{
                    '--thickness': '0.25em'
                  }}>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range />
                      </ProgressCircle.Circle>
                      <AbsoluteCenter>
                        {progress.percentComplete === 100 ? <Icon as={CheckIcon} color={'colorPalette.solid'} /> : <Text fontSize={'2xs'} textStyle={'label'}>
                            {progress.completedSteps}/{progress.totalSteps}
                          </Text>}
                      </AbsoluteCenter>
                    </ProgressCircle.Root>
                    <Heading color={'colorPalette.fg'} size={'md'}>
                      {section_0.title}
                    </Heading>
                  </HStack>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody>
                    <Component groupRequest={groupRequest} upsertGroupRequest={upsertGroupRequest} upsertGroupRequestMutation={upsertGroupRequestMutation} upsertInvestorSearch={upsertInvestorSearch} upsertInvestorSearchMutation={upsertInvestorSearchMutation} upsertUserSearch={upsertUserSearch} upsertUserSearchMutation={upsertUserSearchMutation} onNextSection={() => {
                  setActiveIndex(activeIndex + 1);
                }} onPrevSection={() => {
                  setActiveIndex(activeIndex - 1);
                }} />
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>;
        })}
        </Accordion.Root>
        {/* {!props.hidePublish && (
          <Center padding={4}>
            <PublishRequestButton
              groupRequestId={groupRequest.groupRequestId}
              size={'md'}
              variant={'cta'}
              width={'full'}
            />
          </Center>
         )} */}
      </Box>
    </VStack>;
};